import React from 'react';
import { IconButton } from './Button';
import Select from './Select';
import { Icon } from '../icons';
import { useT } from '../../i18n';

const DOTS = 'dots';

/** Build the page list with leading/trailing boundaries and sibling window. */
function getPageItems(page, pageCount, siblingCount = 1, boundaryCount = 1) {
  const total = pageCount;
  // If few enough pages, show them all.
  if (total <= boundaryCount * 2 + siblingCount * 2 + 3) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const startPages = Array.from({ length: boundaryCount }, (_, i) => i + 1);
  const endPages = Array.from({ length: boundaryCount }, (_, i) => total - boundaryCount + 1 + i);
  const left = Math.max(page - siblingCount, boundaryCount + 2);
  const right = Math.min(page + siblingCount, total - boundaryCount - 1);
  const middle = Array.from({ length: Math.max(right - left + 1, 0) }, (_, i) => left + i);

  const items = [...startPages];
  if (left > boundaryCount + 2) items.push(DOTS);
  else if (boundaryCount + 1 < total - boundaryCount) items.push(boundaryCount + 1);
  items.push(...middle);
  if (right < total - boundaryCount - 1) items.push(DOTS);
  else if (total - boundaryCount > boundaryCount) items.push(total - boundaryCount);
  items.push(...endPages);
  return items;
}

/**
 * Pagination — page navigation for tables and lists.
 *
 * Props:
 *   page: number (1-based), pageCount: number
 *   onPageChange(page)
 *   siblingCount, boundaryCount
 *   totalItems, pageSize          — optional, renders a "1–10 of 57" range label
 *   pageSizeOptions: number[]     — optional, renders a page-size Select
 *   onPageSizeChange(size)
 */
const Pagination = ({
  page = 1,
  pageCount = 1,
  onPageChange,
  siblingCount = 1,
  boundaryCount = 1,
  totalItems,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  className = '',
}) => {
  const t = useT();
  const items = getPageItems(page, pageCount, siblingCount, boundaryCount);
  const go = (p) => { if (p >= 1 && p <= pageCount && p !== page) onPageChange?.(p); };

  const rangeLabel = (() => {
    if (totalItems == null || !pageSize) return null;
    const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, totalItems);
    return t('{start}–{end} of {total}', { start, end, total: totalItems });
  })();

  return (
    <div className={`ps-pagination ${className}`.trim()}>
      <div className="ps-pagination__info">
        {rangeLabel && <span className="ps-pagination__range">{rangeLabel}</span>}
        {pageSizeOptions && onPageSizeChange && (
          <label className="ps-pagination__pagesize">
            <span>{t('Rows')}</span>
            <Select
              size="sm"
              ariaLabel={t('Rows per page')}
              matchTriggerWidth={false}
              value={pageSize}
              onChange={(v) => onPageSizeChange(Number(v))}
              options={pageSizeOptions.map((n) => ({ value: n, label: String(n) }))}
            />
          </label>
        )}
      </div>

      <nav className="ps-pagination__pages" aria-label={t('Pagination')}>
        <IconButton aria-label={t('Previous page')} variant="ghost" size="icon-sm" disabled={page <= 1} onClick={() => go(page - 1)}>
          <Icon.ChevronLeft />
        </IconButton>
        {items.map((it, i) =>
          it === DOTS ? (
            <span key={`d${i}`} className="ps-pagination__dots" aria-hidden="true"><Icon.MoreHorizontal size={16} /></span>
          ) : (
            <button
              key={it}
              type="button"
              className={`ps-pagination__page${it === page ? ' is-active' : ''}`}
              aria-current={it === page ? 'page' : undefined}
              onClick={() => go(it)}
            >
              {it}
            </button>
          ),
        )}
        <IconButton aria-label={t('Next page')} variant="ghost" size="icon-sm" disabled={page >= pageCount} onClick={() => go(page + 1)}>
          <Icon.ChevronRight />
        </IconButton>
      </nav>
    </div>
  );
};

export default Pagination;
